import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
    IconLoader2, IconCheck, IconX,
    IconAlertCircle, IconClipboardList, IconReceipt,
    IconCoins, IconFileText,
} from '@tabler/icons-react';
import { useAuth } from '../../contexts/AuthContext';
import Breadcrumb from '../../components/common/Breadcrumb';
import {
    usePendingTravelRequests,
    usePendingTravelBudgets,
    usePendingTravelAdvances,
    usePendingTravelPRVs,
    useApproveTravelRequestLM,
    useApproveTravelRequestSM,
    useApproveTravelBudgetLM,
    useApproveTravelBudgetFinance,
    useApproveTravelAdvanceFinance,
    useMarkTravelAdvancePaid,
    useApproveTravelPRVFinance,
    useApproveTravelPRVPartner,
    useMarkTravelPRVPaid,
} from '../../hooks/useTravel';

// ─── Helpers 

const CURRENCY_SYMBOLS = { NGN: '₦', USD: '$', EUR: '€', GBP: '£', CAD: 'C$', ZAR: 'R', KES: 'KSh', GHS: 'GH₵' };
const fmt = (v, curr = 'NGN') => {
    const symbol = CURRENCY_SYMBOLS[curr] || curr;
    return `${symbol}${Number(v || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const STATUS_BADGE = {
    draft: 'bg-bg-light text-text-secondary border border-border',
    submitted: 'bg-sky-500/10 text-sky-500 border border-sky-500/30',
    approved: 'bg-success/10 text-success border border-success/30',
    rejected: 'bg-danger/10 text-danger border border-danger/30',
    paid: 'bg-purple-500/10 text-purple-500 border border-purple-500/30',
};

const STATUS_LABELS = {
    draft: 'Draft',
    submitted: 'Submitted',
    approved: 'Approved',
    rejected: 'Rejected',
    paid: 'Paid',
};

function StatusBadge({ status }) {
    const cls = STATUS_BADGE[status] || 'bg-bg-light text-text-secondary border border-border';
    return (
        <span className={`px-8 py-3 rounded-full text-xs font-medium ${cls}`}>
            {STATUS_LABELS[status] || status}
        </span>
    );
}

const AVATAR_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#f43f5e', '#06b6d4'];

function getInitials(name) {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

function Avatar({ name, size = 'sm' }) {
    const bg = AVATAR_COLORS[(name?.charCodeAt(0) || 0) % AVATAR_COLORS.length];
    const dim = size === 'sm' ? 'w-[32px] h-[32px] text-xs' : 'w-[40px] h-[40px] text-sm';
    return (
        <div
            style={{ backgroundColor: bg }}
            className={`${dim} rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0`}
        >
            {getInitials(name)}
        </div>
    );
}

// ─── Approval Comment Modal 

function ApprovalModal({ isOpen, onClose, title, requireComment, onConfirm, isLoading }) {
    const [comment, setComment] = useState('');

    const handleConfirm = () => {
        if (requireComment && !comment.trim()) {
            toast.error('Please provide a comment/reason.');
            return;
        }
        onConfirm(comment.trim());
    };

    if (!isOpen) return null;
    return (
        <div
            className="fixed inset-0 z-[1001] flex items-center justify-center bg-black/50 backdrop-blur-sm p-15"
            onClick={onClose}
        >
            <div
                className="bg-bg-dark rounded-lg shadow-light border border-border w-full max-w-[440px]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-20 border-b border-border">
                    <h5 className="text-[15px] font-semibold text-center">{title}</h5>
                </div>
                <div className="p-20 flex flex-col gap-15">
                    <div className="form-group">
                        <label className="block text-sm font-medium mb-5">
                            Comment {requireComment && <span className="text-danger">*</span>}
                        </label>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder={requireComment ? 'Reason is required...' : 'Optional comment...'}
                            rows={3}
                            className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none resize-none"
                        />
                    </div>
                </div>
                <div className="flex gap-10 p-20 border-t border-border">
                    <button onClick={onClose} className="btn btn-outline-secondary flex-1" disabled={isLoading}>
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={isLoading}
                        className="btn btn-primary flex-1 flex items-center justify-center gap-5"
                    >
                        {isLoading ? <IconLoader2 size={15} className="animate-spin" /> : <IconCheck size={15} />}
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Section Tab Button 

function TabButton({ active, onClick, icon: Icon, label, count }) {
    return (
        <button
            onClick={onClick}
            className={`flex items-center gap-8 px-16 py-10 rounded-lg text-sm font-medium transition-all
                ${active
                    ? 'bg-primary text-white shadow-sm'
                    : 'bg-bg-light text-text-secondary hover:text-text-main hover:bg-bg-dark border border-border'
                }`}
        >
            <Icon size={16} />
            {label}
            {count > 0 && (
                <span className={`px-6 py-1 rounded-full text-[10px] font-bold ${active ? 'bg-white/20 text-white' : 'bg-primary/10 text-primary'}`}>
                    {count}
                </span>
            )}
        </button>
    );
}

// ─── Requests Section

function RequestsSection({ pending, orgId, canSecurity, refetch }) {
    const approveLM = useApproveTravelRequestLM();
    const approveSM = useApproveTravelRequestSM();

    const [approvalModal, setApprovalModal] = useState({ open: false, item: null, action: null, hookFn: null, roleLabel: null });

    const openModal = (item, action, hookFn, roleLabel) => {
        setApprovalModal({ open: true, item, action, hookFn, roleLabel });
    };

    const handleConfirm = (comment) => {
        const { item, action, hookFn } = approvalModal;
        hookFn.mutate({ id: item.id, data: { action, comment }, orgId }, {
            onSuccess: () => {
                toast.success(`Successfully ${action}d${approvalModal.roleLabel ? ` by ${approvalModal.roleLabel}` : ''}.`);
                setApprovalModal({ open: false });
                refetch();
            },
            onError: (err) => toast.error(err?.response?.data?.detail || err.message || 'Action failed.'),
        });
    };

    const lmItems = pending?.as_line_manager || [];
    const smItems = pending?.as_security_manager || [];
    // Each user only sees items for their own role 
    const allItems = [
        ...(lmItems.length > 0 && !canSecurity ? lmItems.map(i => ({ ...i, _role: 'lm' })) : []),
        ...(canSecurity ? smItems.map(i => ({ ...i, _role: 'sm' })) : []),
    ];

    if (allItems.length === 0) {
        return (
            <div className="py-30 text-center text-text-secondary text-sm">
                No pending travel requests for your review.
            </div>
        );
    }

    return (
        <>
            <div className="flex flex-col gap-10">
                {allItems.map((item) => {
                    const hookFn = item._role === 'lm' ? approveLM : approveSM;
                    const roleLabel = item._role === 'lm' ? 'Line Manager' : 'Security Manager';
                    const legs = item.legs || [];
                    const destinations = legs.map(l => l.state_city || l.destination).filter(Boolean).join(' → ') || '-';
                    const firstDate = legs[0]?.departure_date
                        ? new Date(legs[0].departure_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                        : '-';

                    return (
                        <div
                            key={`${item._role}-${item.id}`}
                            className="bg-bg-light rounded-lg border border-border p-15 flex flex-wrap items-center gap-15 hover:border-primary/30 transition-all"
                        >
                            <Avatar name={item.requested_by_name || 'User'} />
                            <div className="flex-grow min-w-0">
                                <div className="flex flex-wrap items-center gap-8 mb-3">
                                    <p className="font-medium text-sm">{item.purpose || 'Travel Request'}</p>
                                    <StatusBadge status={item.status} />
                                    <span className="text-[10px] bg-sky-500/10 text-sky-500 px-6 py-2 rounded-full font-medium">{roleLabel}</span>
                                </div>
                                <p className="text-xs text-text-secondary">
                                    {item.requested_by_name} · {destinations} · {firstDate}
                                </p>
                            </div>
                            <div className="flex items-center gap-8 flex-shrink-0">
                                <button
                                    onClick={() => openModal(item, 'approve', hookFn, roleLabel)}
                                    className="btn btn-primary text-xs px-12 py-7 flex items-center gap-5"
                                >
                                    <IconCheck size={13} /> Approve
                                </button>
                                <button
                                    onClick={() => openModal(item, 'reject', hookFn, roleLabel)}
                                    className="btn btn-danger text-xs px-12 py-7 flex items-center gap-5"
                                >
                                    <IconX size={13} /> Reject
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            <ApprovalModal
                isOpen={approvalModal.open}
                onClose={() => setApprovalModal({ open: false })}
                title={approvalModal.action === 'approve' ? 'Approve Travel Request' : 'Reject Travel Request'}
                requireComment={approvalModal.action === 'reject'}
                onConfirm={handleConfirm}
                isLoading={approveLM.isPending || approveSM.isPending}
            />
        </>
    );
}

// ─── Budgets Section 

function BudgetsSection({ pending, orgId, canFinance, refetch }) {
    const approveLM = useApproveTravelBudgetLM();
    const approveFinance = useApproveTravelBudgetFinance();

    const [approvalModal, setApprovalModal] = useState({ open: false, item: null, action: null, hookFn: null, roleLabel: null });

    const openModal = (item, action, hookFn, roleLabel) => {
        setApprovalModal({ open: true, item, action, hookFn, roleLabel });
    };

    const handleConfirm = (comment) => {
        const { item, action, hookFn } = approvalModal;
        hookFn.mutate({ id: item.id, data: { action, comment }, orgId }, {
            onSuccess: () => {
                toast.success(`Successfully ${action}d${approvalModal.roleLabel ? ` by ${approvalModal.roleLabel}` : ''}.`);
                setApprovalModal({ open: false });
                refetch();
            },
            onError: (err) => toast.error(err?.response?.data?.detail || err.message || 'Action failed.'),
        });
    };

    const lmItems = pending?.as_line_manager || [];
    const finItems = pending?.as_finance_director || [];
    const allItems = [
        ...(canFinance ? finItems.map(i => ({ ...i, _role: 'finance' })) : lmItems.map(i => ({ ...i, _role: 'lm' }))),
    ];

    if (allItems.length === 0) {
        return (
            <div className="py-30 text-center text-text-secondary text-sm">
                No pending travel budgets for your review.
            </div>
        );
    }

    return (
        <>
            <div className="flex flex-col gap-10">
                {allItems.map((item) => {
                    const hookFn = item._role === 'lm' ? approveLM : approveFinance;
                    const roleLabel = item._role === 'lm' ? 'Line Manager' : 'Finance Director';
                    const budgetCurrency = (item.items?.[0]?.currency) || item.currency || 'NGN';
                    const budgetTotal = (item.items || []).reduce((sum, i) => sum + parseFloat(i.total || 0), 0);
                    const totalStr = fmt(budgetTotal, budgetCurrency);

                    return (
                        <div
                            key={`${item._role}-${item.id}`}
                            className="bg-bg-light rounded-lg border border-border p-15 flex flex-wrap items-center gap-15 hover:border-primary/30 transition-all"
                        >
                            <Avatar name={item.requested_by_name || item.employee_name || 'User'} />
                            <div className="flex-grow min-w-0">
                                <div className="flex flex-wrap items-center gap-8 mb-3">
                                    <p className="font-medium text-sm">Budget — {item.travel_request_purpose || `TRV-${String(item.travel_request || item.id).padStart(4, '0')}`}</p>
                                    <StatusBadge status={item.status} />
                                    <span className="text-[10px] bg-sky-500/10 text-sky-500 px-6 py-2 rounded-full font-medium">{roleLabel}</span>
                                </div>
                                <p className="text-xs text-text-secondary">
                                    {item.requested_by_name || item.employee_name || '-'} · Total: {totalStr}
                                </p>
                            </div>
                            <div className="flex items-center gap-8 flex-shrink-0">
                                <button
                                    onClick={() => openModal(item, 'approve', hookFn, roleLabel)}
                                    className="btn btn-primary text-xs px-12 py-7 flex items-center gap-5"
                                >
                                    <IconCheck size={13} /> Approve
                                </button>
                                <button
                                    onClick={() => openModal(item, 'reject', hookFn, roleLabel)}
                                    className="btn btn-danger text-xs px-12 py-7 flex items-center gap-5"
                                >
                                    <IconX size={13} /> Reject
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            <ApprovalModal
                isOpen={approvalModal.open}
                onClose={() => setApprovalModal({ open: false })}
                title={approvalModal.action === 'approve' ? 'Approve Budget' : 'Reject Budget'}
                requireComment={approvalModal.action === 'reject'}
                onConfirm={handleConfirm}
                isLoading={approveLM.isPending || approveFinance.isPending}
            />
        </>
    );
}

// ─── Advances Section

function AdvancesSection({ pending, orgId, canFinance, refetch }) {
    const approveFinance = useApproveTravelAdvanceFinance();
    const markPaid = useMarkTravelAdvancePaid();

    const [approvalModal, setApprovalModal] = useState({ open: false, item: null, action: null });
    const [approvedAmount, setApprovedAmount] = useState('');

    const openModal = (item, action) => {
        setApprovalModal({ open: true, item, action });
        setApprovedAmount('');
    };

    const handleConfirm = (comment) => {
        const { item, action } = approvalModal;
        const data = {
            action,
            comment,
            ...(approvedAmount ? { approved_amount: parseFloat(approvedAmount) } : {}),
        };
        approveFinance.mutate({ id: item.id, data, orgId }, {
            onSuccess: () => {
                if (action === 'approve') {
                    // Chain mark_paid immediately after finance approval
                    markPaid.mutate({ id: item.id, orgId }, {
                        onSuccess: () => { toast.success('Advance approved and marked as paid.'); setApprovalModal({ open: false }); refetch(); },
                        onError: (err) => toast.error(err?.response?.data?.detail || err.message || 'Approved but failed to mark as paid.'),
                    });
                } else {
                    toast.success('Advance rejected by Finance Director.');
                    setApprovalModal({ open: false });
                    refetch();
                }
            },
            onError: (err) => toast.error(err?.response?.data?.detail || err.message || 'Action failed.'),
        });
    };

    const allItems = canFinance ? (pending?.as_finance_director || []) : [];

    if (allItems.length === 0) {
        return (
            <div className="py-30 text-center text-text-secondary text-sm">
                No pending travel advances for your review.
            </div>
        );
    }

    return (
        <>
            <div className="flex flex-col gap-10">
                {allItems.map((item) => (
                    <div
                        key={item.id}
                        className="bg-bg-light rounded-lg border border-border p-15 flex flex-wrap items-center gap-15 hover:border-primary/30 transition-all"
                    >
                        <Avatar name={item.requested_by_name || item.employee_name || 'User'} />
                        <div className="flex-grow min-w-0">
                            <div className="flex flex-wrap items-center gap-8 mb-3">
                                <p className="font-medium text-sm">Advance — {item.travel_request_purpose || `TRV-${String(item.travel_request || item.id).padStart(4, '0')}`}</p>
                                <StatusBadge status={item.status} />
                                <span className="text-[10px] bg-sky-500/10 text-sky-500 px-6 py-2 rounded-full font-medium">Finance Director</span>
                            </div>
                            <p className="text-xs text-text-secondary">
                                {item.requested_by_name || item.employee_name || '-'} · Requested: {fmt(item.requested_amount, item.currency)}
                                {item.approved_amount ? ` · Approved: ${fmt(item.approved_amount, item.currency)}` : ''}
                            </p>
                        </div>
                        <div className="flex items-center gap-8 flex-shrink-0">
                            <button
                                onClick={() => openModal(item, 'approve')}
                                className="btn btn-primary text-xs px-12 py-7 flex items-center gap-5"
                            >
                                <IconCheck size={13} /> Approve & Pay
                            </button>
                            <button
                                onClick={() => openModal(item, 'reject')}
                                className="btn btn-danger text-xs px-12 py-7 flex items-center gap-5"
                            >
                                <IconX size={13} /> Reject
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Approval Modal with optional approved amount field */}
            {approvalModal.open && (
                <div
                    className="fixed inset-0 z-[1001] flex items-center justify-center bg-black/50 backdrop-blur-sm p-15"
                    onClick={() => setApprovalModal({ open: false })}
                >
                    <div
                        className="bg-bg-dark rounded-lg shadow-light border border-border w-full max-w-[440px]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-20 border-b border-border">
                            <h5 className="text-[15px] font-semibold text-center">
                                {approvalModal.action === 'approve' ? 'Approve & Pay Advance' : 'Reject Advance'}
                            </h5>
                        </div>
                        <div className="p-20 flex flex-col gap-15">
                            {approvalModal.action === 'approve' && (
                                <div className="form-group">
                                    <label className="flex items-center gap-5 text-sm font-medium mb-5"><IconCoins size={14} /> Approved Amount — optional override</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={approvedAmount}
                                        onChange={e => setApprovedAmount(e.target.value)}
                                        placeholder={approvalModal.item?.requested_amount || 'Leave blank to use requested amount'}
                                        className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none"
                                    />
                                </div>
                            )}
                            <div className="form-group">
                                <label className="block text-sm font-medium mb-5">
                                    Comment {approvalModal.action === 'reject' && <span className="text-danger">*</span>}
                                </label>
                                <ApprovalCommentTextarea
                                    requireComment={approvalModal.action === 'reject'}
                                    onConfirm={handleConfirm}
                                    onClose={() => setApprovalModal({ open: false })}
                                    isLoading={approveFinance.isPending || markPaid.isPending}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function ApprovalCommentTextarea({ requireComment, onConfirm, onClose, isLoading }) {
    const [comment, setComment] = useState('');
    const handleConfirm = () => {
        if (requireComment && !comment.trim()) {
            toast.error('Please provide a reason.');
            return;
        }
        onConfirm(comment.trim());
    };
    return (
        <>
            <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={requireComment ? 'Reason is required...' : 'Optional comment...'}
                rows={3}
                className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none resize-none"
            />
            <div className="flex gap-10 mt-10">
                <button onClick={onClose} className="btn btn-outline-secondary flex-1" disabled={isLoading}>Cancel</button>
                <button onClick={handleConfirm} disabled={isLoading} className="btn btn-primary flex-1 flex items-center justify-center gap-5">
                    {isLoading ? <IconLoader2 size={15} className="animate-spin" /> : <IconCheck size={15} />}
                    Confirm
                </button>
            </div>
        </>
    );
}

// ─── PRVs Section 

function PRVsSection({ pending, orgId, canFinance, canPartner, refetch }) {
    const approveFinance = useApproveTravelPRVFinance();
    const approvePartner = useApproveTravelPRVPartner();
    const markPaid = useMarkTravelPRVPaid();

    const [approvalModal, setApprovalModal] = useState({ open: false, item: null, action: null, hookFn: null, roleLabel: null });

    const openModal = (item, action, hookFn, roleLabel) => {
        setApprovalModal({ open: true, item, action, hookFn, roleLabel });
    };

    const handleConfirm = (comment) => {
        const { item, action, hookFn } = approvalModal;
        hookFn.mutate({ id: item.id, data: { action, comment }, orgId }, {
            onSuccess: () => {
                toast.success(`Successfully ${action}d${approvalModal.roleLabel ? ` by ${approvalModal.roleLabel}` : ''}.`);
                setApprovalModal({ open: false });
                refetch();
            },
            onError: (err) => toast.error(err?.response?.data?.detail || err.message || 'Action failed.'),
        });
    };

    const handleMarkPaid = (item) => {
        markPaid.mutate({ id: item.id, orgId }, {
            onSuccess: () => { toast.success('PRV marked as paid.'); refetch(); },
            onError: (err) => toast.error(err?.response?.data?.detail || err.message || 'Failed.'),
        });
    };

    const finItems = pending?.as_finance_director || [];
    const partnerItems = pending?.as_managing_partner_coo || [];
    const allItems = [
        ...(canFinance ? finItems.map(i => ({ ...i, _role: 'finance' })) : []),
        ...(canPartner ? partnerItems.map(i => ({ ...i, _role: 'partner' })) : []),
    ];

    if (allItems.length === 0) {
        return (
            <div className="py-30 text-center text-text-secondary text-sm">
                No pending PRVs for your review.
            </div>
        );
    }

    return (
        <>
            <div className="flex flex-col gap-10">
                {allItems.map((item) => {
                    const hookFn = item._role === 'finance' ? approveFinance : approvePartner;
                    const roleLabel = item._role === 'finance' ? 'Finance Director' : 'Managing Partner/COO';
                    const isApproved = item.status === 'approved';
                    const expItems = item.expense_items || [];
                    const prvTotalsByCurrency = expItems.reduce((acc, i) => {
                        const curr = i.currency || item.currency || 'NGN';
                        acc[curr] = (acc[curr] || 0) + parseFloat(i.amount || 0);
                        return acc;
                    }, {});
                    const prvTotalStr = Object.entries(prvTotalsByCurrency).map(([curr, val]) => fmt(val, curr)).join(' + ') || fmt(0);

                    return (
                        <div
                            key={`${item._role}-${item.id}`}
                            className="bg-bg-light rounded-lg border border-border p-15 flex flex-wrap items-center gap-15 hover:border-primary/30 transition-all"
                        >
                            <Avatar name={item.requested_by_name || item.employee_name || 'User'} />
                            <div className="flex-grow min-w-0">
                                <div className="flex flex-wrap items-center gap-8 mb-3">
                                    <p className="font-medium text-sm">PRV — {item.travel_request_purpose || `TRV-${String(item.travel_request || item.id).padStart(4, '0')}`}</p>
                                    <StatusBadge status={item.status} />
                                    <span className="text-[10px] bg-sky-500/10 text-sky-500 px-6 py-2 rounded-full font-medium">{roleLabel}</span>
                                </div>
                                <p className="text-xs text-text-secondary">
                                    {item.requested_by_name || item.employee_name || '-'} · Total Expenses: {prvTotalStr}
                                    {item.wht_percent ? ` · WHT: ${item.wht_percent}%` : ''}
                                </p>
                            </div>
                            <div className="flex items-center gap-8 flex-shrink-0">
                                {!isApproved && (
                                    <>
                                        <button
                                            onClick={() => openModal(item, 'approve', hookFn)}
                                            className="btn btn-primary text-xs px-12 py-7 flex items-center gap-5"
                                        >
                                            <IconCheck size={13} /> Approve
                                        </button>
                                        <button
                                            onClick={() => openModal(item, 'reject', hookFn)}
                                            className="btn btn-danger text-xs px-12 py-7 flex items-center gap-5"
                                        >
                                            <IconX size={13} /> Reject
                                        </button>
                                    </>
                                )}
                                {isApproved && (
                                    <button
                                        onClick={() => handleMarkPaid(item)}
                                        disabled={markPaid.isPending}
                                        className="btn btn-primary text-xs px-12 py-7 flex items-center gap-5"
                                    >
                                        {markPaid.isPending ? <IconLoader2 size={13} className="animate-spin" /> : <IconCoins size={13} />}
                                        Mark Paid
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            <ApprovalModal
                isOpen={approvalModal.open}
                onClose={() => setApprovalModal({ open: false })}
                title={approvalModal.action === 'approve' ? 'Approve PRV' : 'Reject PRV'}
                requireComment={approvalModal.action === 'reject'}
                onConfirm={handleConfirm}
                isLoading={approveFinance.isPending || approvePartner.isPending}
            />
        </>
    );
}

// ─── Main Page 

export default function TravelApprovals() {
    const { user, hasAnyPermission } = useAuth();
    const orgId = user?.orgId;

    const canSecurity = hasAnyPermission(['rbacapp.trv_security']);
    const canFinance = hasAnyPermission(['rbacapp.trv_finance']);
    const canPartner = hasAnyPermission(['rbacapp.trv_partner']);
    const canViewAll = hasAnyPermission(['rbacapp.trv_view_all']); // Line Manager

    const { data: pendingRequests, isLoading: loadingReqs, refetch: refetchReqs } = usePendingTravelRequests(orgId);
    const { data: pendingBudgets, isLoading: loadingBudgets, refetch: refetchBudgets } = usePendingTravelBudgets(orgId);
    const { data: pendingAdvances, isLoading: loadingAdv, refetch: refetchAdv } = usePendingTravelAdvances(orgId);
    const { data: pendingPRVs, isLoading: loadingPRVs, refetch: refetchPRVs } = usePendingTravelPRVs(orgId);

    const [activeTab, setActiveTab] = useState('requests');

    // Deduplicate by ID — same item can appear in multiple role arrays
    const uniqueCount = (arrays) => new Set(arrays.flat().map(i => i?.id).filter(Boolean)).size;
    const countRequests = canSecurity
        ? (pendingRequests?.as_security_manager?.length || 0)
        : uniqueCount([pendingRequests?.as_line_manager || []]);
    const countBudgets = canFinance
        ? (pendingBudgets?.as_finance_director?.length || 0)
        : (pendingBudgets?.as_line_manager?.length || 0);
    const countAdvances = canFinance ? (pendingAdvances?.as_finance_director?.length || 0) : 0;
    const countPRVs = uniqueCount([
        ...(canFinance ? [pendingPRVs?.as_finance_director || []] : []),
        ...(canPartner ? [pendingPRVs?.as_managing_partner_coo || []] : []),
    ]);

    const isLoading = loadingReqs || loadingBudgets || loadingAdv || loadingPRVs;

    const canAny = canSecurity || canFinance || canPartner || canViewAll;

    const refetchAll = () => {
        refetchReqs();
        refetchBudgets();
        refetchAdv();
        refetchPRVs();
    };

    // Only block access after data has loaded and confirmed no pending items for any role
    if (!isLoading && !canAny) {
        return (
            <div className="flex flex-col min-h-screen gap-20">
                <Breadcrumb title="Travel Approvals" path="Travel / Approvals" />
                <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15 text-center">
                    <IconAlertCircle size={32} className="text-warning mx-auto mb-10" />
                    <p className="text-text-secondary text-sm">You have no pending travel approvals.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-screen gap-20">
            <Breadcrumb title="Travel Approvals" path="Travel / Approvals" />

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-15 mb-20">
                {[
                    { label: 'Requests', count: countRequests, icon: IconClipboardList, color: 'text-sky-500' },
                    { label: 'Budgets', count: countBudgets, icon: IconFileText, color: 'text-warning' },
                    { label: 'Advances', count: countAdvances, icon: IconCoins, color: 'text-success' },
                    { label: 'PRVs', count: countPRVs, icon: IconReceipt, color: 'text-purple-500' },
                ].map((stat) => (
                    <div key={stat.label} className="bg-bg-dark rounded-lg shadow-light border border-border p-15">
                        <div className="flex items-center gap-10">
                            <div className="w-[40px] h-[40px] rounded-lg bg-bg-light flex items-center justify-center flex-shrink-0">
                                <stat.icon size={20} className={stat.color} />
                            </div>
                            <div>
                                <p className="text-xs text-text-secondary">{stat.label}</p>
                                <p className="text-xl font-bold">{isLoading ? '—' : stat.count}</p>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-8 mb-20">
                <TabButton
                    active={activeTab === 'requests'}
                    onClick={() => setActiveTab('requests')}
                    icon={IconClipboardList}
                    label="Requests"
                    count={countRequests}
                />
                <TabButton
                    active={activeTab === 'budgets'}
                    onClick={() => setActiveTab('budgets')}
                    icon={IconFileText}
                    label="Budgets"
                    count={countBudgets}
                />
                <TabButton
                    active={activeTab === 'advances'}
                    onClick={() => setActiveTab('advances')}
                    icon={IconCoins}
                    label="Advances"
                    count={countAdvances}
                />
                <TabButton
                    active={activeTab === 'prvs'}
                    onClick={() => setActiveTab('prvs')}
                    icon={IconReceipt}
                    label="PRVs"
                    count={countPRVs}
                />
            </div>

            {/* Content Card */}
            <div className="bg-bg-dark rounded-lg shadow-light border border-border md:p-20 p-15">
                {isLoading ? (
                    <div className="flex items-center justify-center py-40">
                        <IconLoader2 size={28} className="animate-spin text-primary" />
                    </div>
                ) : (
                    <>
                        {activeTab === 'requests' && (
                            <RequestsSection
                                pending={pendingRequests}
                                orgId={orgId}
                                canSecurity={canSecurity}
                                canFinance={canFinance}
                                refetch={refetchAll}
                            />
                        )}
                        {activeTab === 'budgets' && (
                            <BudgetsSection
                                pending={pendingBudgets}
                                orgId={orgId}
                                canFinance={canFinance}
                                refetch={refetchAll}
                            />
                        )}
                        {activeTab === 'advances' && (
                            <AdvancesSection
                                pending={pendingAdvances}
                                orgId={orgId}
                                canFinance={canFinance}
                                refetch={refetchAll}
                            />
                        )}
                        {activeTab === 'prvs' && (
                            <PRVsSection
                                pending={pendingPRVs}
                                orgId={orgId}
                                canFinance={canFinance}
                                canPartner={canPartner}
                                refetch={refetchAll}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
